/**
 * asteroidConstants.ts
 * Tuning knobs for the World 2 asteroid field.
 *
 * Four size tiers (sm/md/lg/xl) map 1:1 to four standalone PNGs in
 * `src/assets/sprites/` (`asteroid-{sm,md,lg,xl}.png`, 16/32/48/64 px square).
 * Small (1 HP) and medium (2 HP) are destructible. Large + extra-large are
 * indestructible obstacles that must be dodged.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module asteroidConstants
 */

/** Asteroid size enum. Values match the sprite suffix so renderer lookups stay literal. */
export type AsteroidSize = 'sm' | 'md' | 'lg' | 'xl';

/** Width/height in logical px for each size tier. Square hitboxes. */
export const ASTEROID_SIZE_PX: Record<AsteroidSize, number> = {
  sm: 16,
  md: 32,
  lg: 48,
  xl: 64,
};

/** HP per size tier. lg/xl are indestructible (-1 sentinel; projectiles are absorbed, never deal damage). */
export const ASTEROID_HP: Record<AsteroidSize, number> = {
  sm: 1,
  md: 2,
  lg: -1,
  xl: -1,
};

/**
 * True when this size is destructible. Indestructible asteroids absorb
 * projectiles without taking damage and must be dodged.
 *
 * @param size - Asteroid size tier (sm / md / lg / xl).
 * @returns `true` when `ASTEROID_HP[size]` is positive, `false` for the
 *          `-1` indestructible sentinel.
 */
export function isDestructible(size: AsteroidSize): boolean {
  return ASTEROID_HP[size] > 0;
}

/**
 * Default leftward drift in px / s relative to world space.
 * Asteroids drift slowly leftward on top of the auto-scrolling camera so they
 * appear to fly past the ship at scroll-speed + drift. Tuning: 40 px/s drift
 * + SPACE_AUTO_SCROLL_SPEED (80) = 120 px/s apparent on-screen velocity at
 * the standard camera rate.
 */
export const ASTEROID_DRIFT_SPEED = 40;

/**
 * Mean seconds between asteroid spawns at base difficulty.
 * Spawner uses a Poisson-ish process: each tick has probability
 * `dt / ASTEROID_SPAWN_INTERVAL_S` of producing one asteroid. At 0.6 s mean,
 * a 60-second level produces ~100 asteroids on average — dense enough
 * that a stationary ship will be hit.
 *
 * DECISION (W2 overhaul): Dropped from 0.9 → 0.6 because the original
 * cadence let the player camp at the top/bottom edge and never see a
 * rock for many seconds.
 */
export const ASTEROID_SPAWN_INTERVAL_S = 0.6;

/**
 * Max simultaneously-active asteroids. AGENTS.md caps pools at 50.
 * 24 leaves headroom under the cap and is well above the expected
 * steady-state in-flight count (~6–10 on a typical screen).
 */
export const ASTEROID_MAX_ACTIVE = 24;

/**
 * Vertical spawn-Y bounds inside the viewport. Asteroids spawn anywhere
 * between these (inclusive) so they never clip the HUD bar (which lives in
 * the top ~16 px) or trail off the visible bottom.
 */
export const ASTEROID_SPAWN_Y_MIN = 16;
export const ASTEROID_SPAWN_Y_MAX_OFFSET = 16;

/**
 * Off-camera cull margin (px). An asteroid whose right edge is more than
 * this far left of the camera left-edge is despawned. One full xl width
 * keeps the largest tier from popping out while still partially visible.
 */
export const ASTEROID_OFFSCREEN_MARGIN = 64;

/**
 * Horizontal spawn offset (px) past the right edge of the camera window.
 * Gives each asteroid a brief off-screen runway so its first on-screen
 * frame is not a sudden pop-in mid-viewport.
 *
 * MUST exceed `SHIP_PROJECTILE_OFFSCREEN_MARGIN` (32 px) by at least one
 * tick of asteroid drift (`ASTEROID_DRIFT_SPEED / TARGET_FPS` ≈ 0.67 px at
 * 40 px/s and 60 fps). The space step calls `updateAsteroidSpawner` →
 * `updateAsteroids` → `resolveShipProjectileAsteroidCollisions`, so a
 * brand-new asteroid drifts left by one tick before collision resolution.
 * If the offset only covers the projectile cull margin, that one-tick drift
 * can put the asteroid's left edge back inside the cull range while it is
 * still entirely off-screen, letting it absorb shots the player cannot see.
 *
 * 40 px = 32 (cull) + 1 (drift @ 40 px/s × 1/60 s) + 7 (headroom). The
 * headroom accommodates future drift-speed bumps up to ~420 px/s before
 * needing a recalculation. See `asteroidSystem.test.ts` regression tests.
 */
export const ASTEROID_SPAWN_X_OFFSET = 40;
